import Heading from '@/common/Heading';
import AlertBanner from '@/components/AlertBanner';
import Button, { ButtonType } from '@/components/Button';
import CountdownTimer from '@/components/CountdownTimer';
import Loader from '@/components/Loader';
import Text from '@/components/Text';
import VerificationCodeInput from '@/components/VerificationCodeInput';
import useCurrentUser from '@/hooks/useCurrentUser';
import AuthService from '@/lib/Auth';
import { firebaseAuth } from '@/lib/Firebase';
import MiscUtils from '@/utils/MiscUtils';
import PhoneUtils from '@/utils/PhoneUtils';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { RecaptchaVerifier } from 'firebase/auth';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

interface IVerifyPhoneProps {
  phoneNumber: string;
  onVerified?: (verified: boolean, newUser?: boolean) => void
}

const RESEND_CODE_INTERVAL = 5;
const VERIFICATION_CODE_LENGTH = 6;

function VerifyPhone({ phoneNumber = '', onVerified }: IVerifyPhoneProps) {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [enableResend, setEnableResend] = useState<boolean>(false);
  const [nextResendDate, setNextResendDate] = useState<Date>(
    dayjs().add(RESEND_CODE_INTERVAL, 'seconds').toDate()
  );
  const displayPhoneNumber = React.useMemo(() => {
    if (!phoneNumber?.trim()) return 'your phone number';
    try {
      const sanitized = PhoneUtils.sanitizePhoneNumber(phoneNumber);
      return PhoneUtils.formatInternational(sanitized);
    } catch {
      const normalized = phoneNumber.trim();
      return normalized.startsWith('+') ? normalized : `+1 ${normalized}`;
    }
  }, [phoneNumber]);

  const mutation = useMutation(AuthService.verifyLoginVerificationCode, {
    onSuccess: async (data) => {
      console.log('CODE VERIFIED SUCCESSFULLY');
      const authToken = await firebaseAuth?.currentUser?.getIdToken(true);
      localStorage.setItem('authToken', authToken as string);
      const localIsNewUser = localStorage?.getItem('isNewUser') === 'true';

      if (data) {
        const { isNewUser } = data
        !localIsNewUser && localStorage.setItem('isNewUser', String(isNewUser));
        onVerified?.(true, isNewUser);
      }
    },
  });

  const resendCodeMutation = useMutation(AuthService.sendLoginVerificationCode, {
    onSuccess: (data) => {
      console.log('RESENT VERIFICATION CODE SUCCCESSFULLY');
      setEnableResend(false);
      setNextResendDate(dayjs().add(RESEND_CODE_INTERVAL, 'seconds').toDate());
    },
    onError: (error) => {
      console.log('ERROR RESENDING VERIFICATION CODE', error);
      // if (MiscUtils.isBrowser()) {
      //   window.recaptchaVerifier.render().then(function (widgetId) {
      //     window.recaptchaVerifier.clear();
      //   });
      // }
    },
  });

  const onSubmit = (verificationCode: string) => {
    const code = verificationCode.replace(/[ \(\)]/g, '');
    mutation.mutate({ verificationCode: code });
  };

  const onChange = (code: string) => {
    if (mutation.error) {
      // Clears the error alert at the bottom if there is any
      mutation.reset();
    }
    if (resendCodeMutation.error) {
      // Clears the error alert at the bottom if there is any
      resendCodeMutation.reset();
    }
    setVerificationCode(code);
  };

  const onResendCode = async (e: React.MouseEvent<HTMLButtonElement>) => {
    resendCodeMutation.mutate({ phoneNumber });
  };

  useEffect(() => {
    if (MiscUtils.isBrowser() && enableResend) {
      // @ts-ignore
      window.recaptchaVerifier = new RecaptchaVerifier(
        'resendVerificationCodeBtn',
        {
          size: 'invisible',
          callback: () => {
            // reCAPTCHA solved, allow signInWithPhoneNumber.
            console.log('RECAPTCHA SOLVED');
          },
        },
        firebaseAuth
      );
    }
  }, [enableResend]);

  const btnDisabled = verificationCode.length !== VERIFICATION_CODE_LENGTH;

  return (
    <div className='h-full w-full max-w-md mx-auto flex flex-col place-content-center'>
      <div className="flex flex-col gap-4 bg-darkglass p-4 rounded-xl">
          <div className="flex flex-col items-center mb-4">
            <Heading as='h4' className="w-auto mx-auto font-game uppercase text-center">
              ENTER VERIFICATION CODE
            </Heading>
            <Text size="sm" className="text-center text-secondary">
              We sent a 6-digit code to {displayPhoneNumber}
            </Text>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            onSubmit(verificationCode)
          }}>
            <VerificationCodeInput
              name="verificationCode"
              length={VERIFICATION_CODE_LENGTH}
              className="w-[56px] h-16"
              placeholder="-"
              loading={false}
              onChange={onChange}
              onComplete={onSubmit}
            />
            <div className="flex flex-col gap-4 mt-4">
              <Button
                type={ButtonType.Primary}
                size='md'
                buttonType="submit"
                className="w-full"
                disabled={btnDisabled}
                loading={mutation.isLoading || mutation.isSuccess}
              >
                {btnDisabled ? 'Verify code' : 'Continue'}
              </Button>
              {enableResend && <Button
                loading={resendCodeMutation.isLoading}
                type={ButtonType.Outlined}
                size='md'
                buttonType="button"
                onClick={onResendCode}
                id="resendVerificationCodeBtn"
              >
                Resend code {!enableResend && <CountdownTimer
                  targetDate={nextResendDate}
                  abbreviatedSuffix
                  expiredMessage="0 seconds"
                  onExpire={async () => setEnableResend(true)}
                />}
              </Button>}
            </div>
          </form>
        <>
          {(mutation.error || resendCodeMutation.error) && (
            <AlertBanner
              type="error"
              message="We ran into an unexpected issue. Please try again later."
              className="mt-4"
            />
          )}
        </>
      </div>
    </div>
  );
}

export default VerifyPhone;
